import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Column } from 'react-table'
import { Box, Button, Checkbox, Container, DialogContent, FormControl, FormControlLabel, Grid, InputLabel, makeStyles, MenuItem, Select, TextField, Typography } from '@material-ui/core'
import DateFnsUtils from '@date-io/date-fns'
import daLocale from "date-fns/locale/da"
import { MuiPickersUtilsProvider } from '@material-ui/pickers'
import Table from '../../components/Table'
import FormDialogWithDelete from "../../components/dialogs/FormDialogWithDelete"
import NoDataDialog from '../../components/dialogs/ErrorDialog'
import moment from "moment"
import "moment/locale/da"
import * as API from '../../api/customermanagement/index'
import * as Models from '../../api/customermanagement/models'
import APIRequest from '../../components/helpers/APIRequest'
import { DateTimePicker } from '@material-ui/pickers'
import { trCard, trDateTime } from '../../utils/tr'

moment.locale("da")

const useStyles = makeStyles((theme) => ({
    paper: {
        [theme.breakpoints.up('sm')]: {
            padding: '0',
        },
        marginTop: theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    buts: {
        width: '100%',
        height: '100%'
    },
    grid: {
        width: '100%', // Fix IE 11 issue.
        marginTop: theme.spacing(3),
    },
    textHelp: {
        marginBottom: '1rem'
    },
    margTop: {
        marginTop: theme.spacing(2),
    }
}))

const TerminalChanges: FC = () => {
    const classes = useStyles()

    const [customers, setCustomers] = useState<Models.CustomersResponse>(new Models.CustomersResponse())
    const [isPendingGetCustomers, setPendingGetCustomers] = useState(false)
    const [runGetCustomers, setRunGetCustomers] = useState(true)
    const [customerId, setCustomerId] = useState(0)

    const [showNoDataDialog, setShowNoDataDialog] = React.useState(false)
    const [showErrorDialog, setShowErrorDialog] = React.useState(0)

    const [terminalChanges, setTerminalChanges] = useState<Models.TerminalChangesResponse>(new Models.TerminalChangesResponse())
    const [isPendingGetTerminalChanges, setPendingGetTerminalChanges] = useState(false)
    const [runGetTerminalChanges, setRunGetTerminalChanges] = useState(false)
    const [runRequestStoreTerminalChanges, setRunRequestStoreTerminalChanges] = useState(false)
    const [isPendingRequestStoreTerminalChanges, setPendingRunRequestStoreTerminalChanges] = useState(false)
    const [runRequestDeleteTerminalChange, setRunRequestDeleteTerminalChange] = useState(false)
    const [isPendingRequestDeleteTerminalChange, setPendingRunRequestDeleteTerminalChange] = useState(false)

    // Dialog and related API control
    const [editTerminalChangeOpen, setEditTerminalChangeOpen] = useState(false)
    const [editDialogTitle, setEditDialogTitle] = useState("")

    // Definitions for the edit dialog
    const [terminalChangeId, setTerminalChangeId] = useState(0)
    const [terminalChangeTerminalId, setTerminalChangeTerminalId] = useState("")
    const [terminalChangePassphrase, setTerminalChangePassphrase] = useState("")
    const [terminalChangeCount, setTerminalChangeCount] = useState(0)
    const [terminalChangeExpires, setTerminalChangeExpires] = useState<null | Date>()

    // Necessary for avoiding strikethrough on the customers combobox
    const labelRef = useRef(null)
    // @ts-ignore: Object is possibly 'null'
    const labelWidth = (labelRef !== null && labelRef.current !== null) ? labelRef.current.clientWidth : 0

    useEffect(() => {
        if (customers.result.length > 0) {
            setCustomerId(customers.result[0].id)
        }
    }, [customers, setCustomerId])

    useEffect(() => {
        if (customerId !== 0) {
            setRunGetTerminalChanges(true)
        }
        setTerminalChanges(new Models.TerminalChangesResponse())
    }, [customerId, setRunGetTerminalChanges, setTerminalChanges])

    const columns: Column<Models.TerminalChange>[] = React.useMemo(() => [
        { Header: 'ID', accessor: "id" }, { Header: 'Automat ID', accessor: 'terminalId'}, { Header: 'Passphrase', accessor: 'passphrase'},
        { Header: 'Tæller', accessor: 'useCount'}, { Header: 'Udløber', accessor: tc => trDateTime(tc.expires) }, { Header: 'Url for rsync', accessor: 'rsyncUrl'}
    ], [])

    const handleClickNewTerminalChange = () => {
        // Run the dialog with a new user
        let tc = new Models.TerminalChange()
        tc.expires.setDate(tc.expires.getDate() + 7) // Default is 7 days in the future
        handleRowClick(tc)
    }

    const handleRowClick = (element: Models.TerminalChange) => {
        if (element.id === 0) {
            setEditDialogTitle("Ny automat ændring")
        } else {
            setEditDialogTitle("Ændring af automat ændring")
        }

        setTerminalChangeId(element.id)
        setTerminalChangeTerminalId(element.terminalId)
        setTerminalChangePassphrase(element.passphrase)
        setTerminalChangeExpires(element.expires)
        setTerminalChangeCount(element.useCount)

        setEditTerminalChangeOpen(true)
    }

    const RequestGetTerminalChanges = () => {
        return (<APIRequest<Models.TerminalChangesResponse>
            run={runGetTerminalChanges} setRun={setRunGetTerminalChanges}
            pending={isPendingGetTerminalChanges} setIsPending={setPendingGetTerminalChanges}
            req={() => API.getTerminalChanges(customerId)}
            setData={setTerminalChanges}
            setShowError={setShowNoDataDialog}
            />
        )
    }

    const RequestStoreTerminalChanges = () => {
        const createTerminalChangeParam = () => {
            return new Models.TerminalChange(terminalChangeId, terminalChangeTerminalId ? terminalChangeTerminalId.trim().toUpperCase() : "",
                terminalChangePassphrase ? terminalChangePassphrase.trim() : "", terminalChangeCount, terminalChangeExpires ? terminalChangeExpires : new Date())
        }
        return (<APIRequest<Models.TerminalChangesResponse>
            run={runRequestStoreTerminalChanges} setRun={setRunRequestStoreTerminalChanges}
            pending={isPendingRequestStoreTerminalChanges} setIsPending={setPendingRunRequestStoreTerminalChanges}
            setData={setTerminalChanges}
            req={() => API.storeTerminalChanges(customerId, createTerminalChangeParam())}
            setShowError={setShowNoDataDialog}
        />)
    }

    const RequestDeleteTerminalChange = () => {
        return (<APIRequest<Models.TerminalChangesResponse>
            run={runRequestDeleteTerminalChange} setRun={setRunRequestDeleteTerminalChange}
            pending={isPendingRequestDeleteTerminalChange} setIsPending={setPendingRunRequestDeleteTerminalChange}
            req={() => API.deleteTerminalChange(customerId, terminalChangeId)}
            setData={setTerminalChanges}
            setShowError={setShowNoDataDialog}
        />)
    }

    const RequestGetCustomers = () => {
        return (<APIRequest<Models.CustomersResponse>
            run={runGetCustomers} setRun={setRunGetCustomers}
            pending={isPendingGetCustomers} setIsPending={setPendingGetCustomers}
            req={() => API.getCustomers()}
            setData={setCustomers}
            setShowError={setShowNoDataDialog}
        />)
    }

    const submitStoreTerminalChange = () => {
        setEditTerminalChangeOpen(false)

        const terminalIdCheck = () => {
            const parts = terminalChangeTerminalId.split("-")
            if (parts.length !== 2) {
                return false
            }
            const regex = new RegExp("^([a-zA-Z0-9]){1,50}$")
            return regex.test(parts[0]) && regex.test(parts[1])
        }

        const passphraseCheck = () => {
            const regex = new RegExp("^([0-9]){1,50}$")
            return regex.test(terminalChangePassphrase)
        }
        if (terminalChangePassphrase.trim().length === 0) {
            setShowErrorDialog(-1)
        } else if (terminalChangeTerminalId.length === 0) {
            setShowErrorDialog(-2)
        } else if (!terminalIdCheck()) {
            setShowErrorDialog(-3)
        } else if (!passphraseCheck()) {
            setShowErrorDialog(-4)
        } else {
            setRunRequestStoreTerminalChanges(true)
        }
    }

    const errorMessage = () => {
        switch (showErrorDialog) {
            case -1: return "Passphrase må ikke være tomt"
            case -2: return "Automat ID må ikke være tomt"
            case -3: return "Automat ID skal være på formen 'ABC123-12'"
            case -4: return "Passphrase må kun have tal"

            case 405: return "Du har ikke rettigheder til at hente disse data"
            case 406: return "Ukendt fejl, kunne ikke gemme oplysningerne. Prøv igen senere."

            case 0: return "" // No error
        }
        return "Der er sket en fejl. Prøv igen senere."
    }

    return (
        <Container maxWidth={false}>
            <RequestGetTerminalChanges />
            <RequestStoreTerminalChanges />
            <RequestDeleteTerminalChange />
            <RequestGetCustomers />

            <div className={classes.paper}>
                <Typography component="h1" variant="h5">Automat ændringer</Typography>
                <Container maxWidth={"lg"} className={classes.margTop}>
                <Grid container spacing={2}>
                    <Grid item sm={4}>
                        <FormControl variant="outlined" fullWidth>
                            <InputLabel ref={labelRef} shrink htmlFor="ps-customerslist">Kunde</InputLabel>
                            <Select labelWidth={labelWidth} variant="outlined" id="ps-customerslist" value={customerId} onChange={(event: React.ChangeEvent<{ value: unknown }>) => {setCustomerId(event.target.value as number)}}>
                                {customers.result.filter((c) => {return c.active}).sort((a, b) => a.navn.localeCompare(b.navn)).map((customer) => {
                                    return <MenuItem key={`CustomerId-${customer.id}`} value={customer.id}>{customer.navn}</MenuItem>
                                })}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item sm={4}>
                        <Button variant="contained" color="primary" className={classes.buts} onClick={handleClickNewTerminalChange}>Tilføj ændring</Button>
                    </Grid>
                </Grid>
                <Box mt={2}>
                    <Table<Models.TerminalChange> columns={columns} data={terminalChanges.result} initialState={{pageSize: 150}} clickEvent={handleRowClick} update={null} pending={false} text={""}/>
                </Box>
                </Container>
            </div>

            <NoDataDialog open={showNoDataDialog} onClose={() => {setShowNoDataDialog(false)}} title={"Intet svar"} text={"Noget gik galt, prøv venligst igen senere."}/>

            <NoDataDialog open={showErrorDialog !== 0} onClose={() => {setShowErrorDialog(0); setEditTerminalChangeOpen(true)}} title={"Fejl"} text={errorMessage()}/>

            <FormDialogWithDelete title={editDialogTitle} open={editTerminalChangeOpen} deleteText="Slet" saveText="Gem" onClose={() => setEditTerminalChangeOpen(false)} onCloseSubmit={submitStoreTerminalChange} onCloseDelete={() => setRunRequestDeleteTerminalChange(true)}>
                <DialogContent>
                    <MuiPickersUtilsProvider locale={daLocale} utils={DateFnsUtils}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12}>
                                <TextField name="terminalId" variant="outlined" fullWidth id="terminalId" label="Automat ID" autoFocus value={terminalChangeTerminalId} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setTerminalChangeTerminalId(e.currentTarget.value)}} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField name="passphrase" variant="outlined" fullWidth id="passphrase" label="Kodeord" value={terminalChangePassphrase} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setTerminalChangePassphrase(e.currentTarget.value)}} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <TextField name="count" variant="outlined" fullWidth id="count" label="Tæller" inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} value={terminalChangeCount} onChange={(e: React.ChangeEvent<HTMLInputElement>) => {setTerminalChangeCount(+e.currentTarget.value)}} />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <DateTimePicker name="expires" inputVariant="outlined" fullWidth id="expires" label="Udløb" format="d/M-yyyy HH:mm" ampm={false} disablePast value={terminalChangeExpires} onChange={setTerminalChangeExpires} />
                            </Grid>
                        </Grid>
                    </MuiPickersUtilsProvider>
                </DialogContent>
            </FormDialogWithDelete>
        </Container>
    )
}

export default TerminalChanges
